import React, { useCallback, useEffect, useState } from 'react';
import { Search, Filter, Eye, MoreHorizontal, Phone, Mail, MapPin, Wrench, Sparkles, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Textarea } from '../ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import {
  deleteCleaningRequest,
  deletePartOrder,
  getCleaningRequests,
  getPartOrders,
  updateCleaningRequest,
  updatePartOrder
} from '../../services/api';
import { useUnread } from '../../context/UnreadContext';
import { useAuth } from '../../context/AuthContext';
import { UnreadDot } from './UnreadBadge';

const CLEANING_SERVICES = {
  echangeur_air: "Nettoyage d'échangeur d'air",
  thermopompe_centrale: 'Nettoyage de thermopompe centrale',
  thermopompe_murale: 'Nettoyage de thermopompe murale'
};

const APPLIANCES = {
  laveuse: 'Laveuse',
  secheuse: 'Sécheuse',
  'lave-vaisselle': 'Lave-vaisselle',
  cuisiniere: 'Cuisinière',
  refrigerateur: 'Réfrigérateur',
  congelateur: 'Congélateur',
  autre: 'Autre'
};

// Configuration par type : mêmes composants, libellés et statuts du backend.
export const REQUEST_TYPES = {
  cleaning_requests: {
    icon: Sparkles,
    avatarClass: 'bg-[#00b4d8]',
    empty: 'Aucune demande de nettoyage trouvée',
    detailsTitle: 'Détails de la demande de nettoyage',
    load: getCleaningRequests,
    update: updateCleaningRequest,
    remove: deleteCleaningRequest,
    deleteTitle: 'Supprimer cette demande de nettoyage?',
    deletedMessage: 'Demande de nettoyage supprimée',
    statuses: [
      { value: 'pending', label: 'En attente', badge: 'badge-pending' },
      { value: 'confirmed', label: 'Confirmé', badge: 'badge-confirmed' },
      { value: 'completed', label: 'Terminé', badge: 'badge-completed' },
      { value: 'cancelled', label: 'Annulé', badge: 'badge-cancelled' }
    ],
    summary: (request) => (request.service_type || []).map(type => CLEANING_SERVICES[type] || type).join(', '),
    searchText: (request) => [request.first_name, request.last_name, request.address].join(' '),
    details: (request) => [
      ['Services', (request.service_type || []).map(type => CLEANING_SERVICES[type] || type).join(', ')],
      ['Adresse', request.address],
      ['Disponibilités', request.availability_text || 'Non spécifiées'],
      ['Description', request.description]
    ]
  },
  part_orders: {
    icon: Wrench,
    avatarClass: 'bg-[#1a3a5c]',
    empty: 'Aucune commande de pièce trouvée',
    detailsTitle: 'Détails de la commande de pièce',
    load: getPartOrders,
    update: updatePartOrder,
    remove: deletePartOrder,
    deleteTitle: 'Supprimer cette commande de pièce?',
    deletedMessage: 'Commande de pièce supprimée',
    statuses: [
      { value: 'pending', label: 'En attente', badge: 'badge-pending' },
      { value: 'contacted', label: 'Contacté', badge: 'badge-confirmed' },
      { value: 'ordered', label: 'Commandée', badge: 'badge-in-progress' },
      { value: 'ready', label: 'Prête', badge: 'badge-in-progress' },
      { value: 'completed', label: 'Terminée', badge: 'badge-completed' }
    ],
    summary: (order) => [APPLIANCES[order.appliance_type] || order.appliance_type, order.brand].filter(Boolean).join(' · '),
    searchText: (order) => [order.first_name, order.last_name, order.brand, order.model, order.part_description].join(' '),
    details: (order) => [
      ['Appareil', APPLIANCES[order.appliance_type] || order.appliance_type],
      ['Marque', order.brand],
      ['Modèle', order.model],
      ['Pièce demandée', order.part_description]
    ]
  }
};

export const CustomerRequestsPanel = ({ kind }) => {
  const config = REQUEST_TYPES[kind];
  const { isUnread, markRead } = useUnread();
  const { can } = useAuth();
  const canEdit = can(`${kind}.edit`);
  const canDelete = can(`${kind}.delete`);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRequests = useCallback(async () => {
    try {
      const response = await config.load();
      setRequests(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(`Error fetching ${kind}:`, error);
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  }, [config, kind]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleUpdate = async (id, data, message) => {
    try {
      await config.update(id, data);
      toast.success(message);
      fetchRequests();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async () => {
    if (!toDelete || deleting) return;
    setDeleting(true);
    try {
      await config.remove(toDelete.id);
      setRequests(current => current.filter(request => request.id !== toDelete.id));
      if (selected?.id === toDelete.id) {
        setDetailsOpen(false);
        setSelected(null);
      }
      markRead(kind, toDelete.id);
      toast.success(config.deletedMessage);
      setToDelete(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const openDetails = (request) => {
    setSelected(request);
    setNotes(request.notes || '');
    setDetailsOpen(true);
    markRead(kind, request.id);
  };

  const statusInfo = (status) =>
    config.statuses.find(item => item.value === status) || { label: status, badge: 'badge-pending' };

  const search = searchTerm.toLowerCase();
  const filtered = requests.filter(request =>
    config.searchText(request).toLowerCase().includes(search) &&
    (statusFilter === 'all' || request.status === statusFilter)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#00b4d8] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid={`${kind}-panel`}>
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {config.statuses.map(item => (
                  <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {filtered.length > 0 ? (
            <div className="divide-y">
              {filtered.map((request) => {
                const status = statusInfo(request.status);
                return (
                  <div key={request.id} className="p-4 hover:bg-slate-50 transition-colors" data-testid={`${kind}-row-${request.id}`}>
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-4">
                          <div className={`relative w-12 h-12 shrink-0 rounded-full ${config.avatarClass} flex items-center justify-center text-white font-semibold`}>
                            {request.first_name?.[0]}{request.last_name?.[0]}
                            <UnreadDot show={isUnread(kind, request.id)} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-[#1a3a5c]">
                              {request.first_name} {request.last_name}
                            </h3>
                            <p className="text-sm text-slate-500">{config.summary(request)}</p>
                            {request.address && (
                              <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                                <MapPin className="w-4 h-4" />
                                {request.address}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <div className="text-sm">
                          <p className="text-slate-500">
                            {new Date(request.created_at).toLocaleDateString('fr-CA')}
                          </p>
                        </div>

                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.badge}`}>
                          {status.label}
                        </span>

                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => openDetails(request)} data-testid={`view-${kind}-${request.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {(canEdit || canDelete) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {canEdit && config.statuses
                                .filter(item => item.value !== request.status)
                                .map(item => (
                                  <DropdownMenuItem
                                    key={item.value}
                                    onClick={() => handleUpdate(request.id, { status: item.value }, 'Statut mis à jour')}
                                  >
                                    {item.label}
                                  </DropdownMenuItem>
                                ))}
                              {canDelete && (<>
                              {canEdit && <div className="my-1 h-px bg-slate-200" />}
                              <DropdownMenuItem
                                onClick={() => setToDelete(request)}
                                className="text-red-700 focus:bg-red-50 focus:text-red-700"
                                data-testid={`delete-${kind}-${request.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer définitivement
                              </DropdownMenuItem>
                              </>)}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <p>{config.empty}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete confirmation */}
      <AlertDialog
        open={Boolean(toDelete)}
        onOpenChange={(open) => {
          if (deleting) return;
          if (!open) setToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{config.deleteTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              La demande de {toDelete?.first_name} {toDelete?.last_name} sera supprimée
              définitivement. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                handleDelete();
              }}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid={`confirm-delete-${kind}`}
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{config.detailsTitle}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Client</p>
                  <p className="font-medium">{selected.first_name} {selected.last_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Téléphone</p>
                  <a href={`tel:${selected.phone}`} className="font-medium flex items-center gap-1 text-[#1a3a5c]">
                    <Phone className="w-4 h-4" />
                    {selected.phone}
                  </a>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-slate-500">Courriel</p>
                  <a href={`mailto:${selected.email}`} className="font-medium flex items-center gap-1 text-[#1a3a5c] break-all">
                    <Mail className="w-4 h-4" />
                    {selected.email}
                  </a>
                </div>
              </div>

              {config.details(selected)
                .filter(([, value]) => value)
                .map(([label, value]) => (
                  <div key={label}>
                    <p className="text-sm text-slate-500">{label}</p>
                    <p className="font-medium bg-slate-50 p-3 rounded-lg">{value}</p>
                  </div>
                ))}

              {canEdit && (
              <div>
                <p className="text-sm text-slate-500 mb-2">Notes internes</p>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ajouter des notes..."
                  className="min-h-[100px]"
                />
                <Button
                  onClick={() => handleUpdate(selected.id, { notes }, 'Notes enregistrées')}
                  className="mt-2 bg-[#00b4d8] hover:bg-[#009bb8]"
                  size="sm"
                >
                  Enregistrer
                </Button>
              </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerRequestsPanel;
